import React, { useRef, useState } from 'react';
import { Tooltip, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import Loading from 'components/Loading';
import './AppDataTable.css';
import SentryWrap from 'components/Sentry';

const skillAreas = ['speaking', 'listening', 'reading', 'writing', 'comprehension', 'literacy', 'oral', 'productive'];

const proficiencyLevels = [
  { key: 'Novice', number: 1, label: 'Novice' },
  { key: 'Early_Intermediate', number: 2, label: 'Early Intermediate' },
  { key: 'Intermediate', number: 3, label: 'Intermediate' },
  { key: 'Advance', number: 4, label: 'Advance' },
  { key: 'Advance_High', number: 5, label: 'Advance High' },
];

const getSkillDisplayName = (skill) => {
  return skill.charAt(0).toUpperCase() + skill.slice(1);
};

const getSkillLabel = (skill) => {
  const display = getSkillDisplayName(skill);
  if (display === 'Oral') return 'Vocal';
  if (display === 'Productive') return 'Active';
  return display;
};

const getSkillAreaLabel = (skill, tooltip = false) => {
  const display = getSkillDisplayName(skill);
  if (display === 'Oral') return tooltip ? 'Vocal (Listening + Speaking)' : 'Vocal';
  if (display === 'Productive') return tooltip ? 'Active (Speaking + Writing)' : 'Active';
  if (display === 'Comprehension') return tooltip ? 'Comprehension (Listening + Reading)' : 'Comprehension';
  if (display === 'Literacy') return tooltip ? 'Literacy (Reading + Writing)' : 'Literacy';
  return display;
};

const formatProficiencyLevel = (level) => {
  if (!level || level === '-') return '-';
  return level.replace(/_/g, ' ');
};

export default function AppDataTableComponent({ overallReport }) {
  const printRef = useRef(null);
  const [printLoader, setPrintLoader] = useState(false);
  const [studentModal, setStudentModal] = useState({
    open: false,
    skillLabel: '',
    levelLabel: '',
    scaleScore: null,
    students: [],
  });

  const printCall = useReactToPrint({
    contentRef: printRef,
    onBeforeGetContent: async () => {
      setPrintLoader(true);
      if (printRef.current) {
        printRef.current.style.height = 'auto';
        printRef.current.style.overflow = 'visible';
      }
    },
    onBeforePrint: async () => {
      setPrintLoader(false);
      if (printRef.current) {
        printRef.current.style.height = '';
        printRef.current.style.overflow = '';
      }
    },
  });

  const handleScaleReportClick = (e) => {
    e.preventDefault();
    console.log('Scale Report clicked');
  };

  const getSkillData = (skill) => {
    if (!overallReport || !overallReport[skill]) {
      return null;
    }
    return overallReport[skill].score || {};
  };

  const getScaleReportValue = (skill) => {
    if (!overallReport || !overallReport.scale_report) {
      return '-';
    }
    return overallReport.scale_report[skill] || '-';
  };

  const handleProficiencyCellClick = (skill, level, skillData) => {
    const students = skillData?.students?.[level.key] || [];
    if (students.length === 0) return;
    setStudentModal({
      open: true,
      skillLabel: getSkillAreaLabel(skill, false),
      levelLabel: formatProficiencyLevel(level.key),
      scaleScore: getScaleReportValue(skill),
      students,
    });
  };

  const closeStudentModal = () => {
    setStudentModal((prev) => ({ ...prev, open: false }));
  };

  return (
    <SentryWrap>
      <div className="skill-report-container printable">
      <div className="skill-report-table-wrapper">
        <table className="skill-report-table">
          <thead>
            <tr>
              <th>Skill Area</th>
              <th className="scale-report-header" onClick={handleScaleReportClick}>
                Scale Report
              </th>
              {proficiencyLevels.map((level) => (
                <th key={level.number} className="proficiency-header">
                  {level.number}
                  <span className="proficiency-label">{level.label}</span>
                </th>
              ))}
              {/* <th className="points-header">Points Lost</th>
              <th className="points-header">Points Obtained</th> */}
              <th className="overall-header">Overall Proficiency Level</th>
            </tr>
          </thead>
          <tbody>
            {skillAreas.map((skill) => {
              const skillData = getSkillData(skill);
              if (!skillData) {
                return (
                  <tr key={skill}>
                    <td className="skill-area-cell">
                      <Tooltip title={getSkillDisplayName(skill)} placement="top">
                        <span>{getSkillLabel(skill)}</span>
                      </Tooltip>
                    </td>
                    <td className="scale-report-cell">{getScaleReportValue(skill)}</td>
                    {proficiencyLevels.map((level) => (
                      <td key={`${skill}-${level.number}`}></td>
                    ))}
                    {/* <td className="points-cell"></td>
                    <td className="points-cell"></td> */}
                    <td className="overall-cell"></td>
                  </tr>
                );
              }

              return (
                <tr key={skill}>
                  <td className="skill-area-cell">
                    <Tooltip title={getSkillAreaLabel(skill, true)} placement="top">
                      <span>{getSkillAreaLabel(skill, false)}</span>
                    </Tooltip>
                  </td>
                  <td className="scale-report-cell">{getScaleReportValue(skill)}</td>
                  {proficiencyLevels.map((level) => {
                    const count = skillData[level.key] || 0;
                    const students = skillData.students?.[level.key] || [];
                    const isClickable = count > 0 && students.length > 0;
                    return (
                      <td
                        key={`${skill}-${level.number}`}
                        className={isClickable ? 'proficiency-cell-clickable' : ''}
                        onClick={() => isClickable && handleProficiencyCellClick(skill, level, skillData)}
                        role={isClickable ? 'button' : undefined}
                        title={isClickable ? 'View students' : undefined}
                      >
                        {count}
                      </td>
                    );
                  })}
                  {/* <td className="points-cell">{Math.floor(skillData.point_lost || 0)}</td>
                  <td className="points-cell">{Math.floor(skillData.points_obtained || 0)}</td> */}
                  <td className="overall-cell">
                    {formatProficiencyLevel(skillData.overall_proficiency_level)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>

      <Dialog open={studentModal.open} onClose={closeStudentModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <span>
            {studentModal.skillLabel} – {studentModal.levelLabel}
          </span>
          <IconButton onClick={closeStudentModal} aria-label="close" size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <table className="skill-report-table" style={{ marginTop: 0 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Student</th>
                <th style={{ textAlign: 'right' }}>Points Obtained</th>
              </tr>
            </thead>
            <tbody>
              {studentModal.students.map((s, idx) => (
                <tr key={s.student_id ?? idx}>
                  <td style={{ textAlign: 'left' }}>
                    {s.studentName || [s.firstName, s.lastName].filter(Boolean).join(' ').trim() || '-'}
                  </td>
                  <td style={{ textAlign: 'right' }}>{s.points_obtained  ?? '-'}{studentModal.scaleScore != null && studentModal.scaleScore !== '-' && ` /${studentModal.scaleScore}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DialogContent>
      </Dialog>
    </SentryWrap>
  );
}
